import { Button, Stack, Typography } from '@mui/material';
import { EmptyUI, StatusPanel, StatusPanelStep } from 'components';
import {
  AC_BASE_REQUEST_STATUS,
  STEP_STATUS_LABEL,
} from 'models/create-request';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppSelector } from 'store/hook';
import { RequestOverview, SittersOverView } from './components';

const Overview = () => {
  const navigate = useNavigate();
  const { baseRequest } = useAppSelector((state) => state.overview);

  const listStep = useMemo(() => {
    const steps: StatusPanelStep[] = [
      {
        label: STEP_STATUS_LABEL.requested,
        active: [
          AC_BASE_REQUEST_STATUS.SEARCH_REQUESTED,
          AC_BASE_REQUEST_STATUS.SEARCH_FOUND,
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.SEARCH_REQUESTED,
          AC_BASE_REQUEST_STATUS.SEARCH_FOUND,
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.sitterMatched,
        active: [
          AC_BASE_REQUEST_STATUS.SEARCH_FOUND,
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.SEARCH_FOUND,
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.sitterAccepted,
        active: [
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.PARENT_ACCEPTED,
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.trialCreated,
        active: [
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.TRIAL_CREATED,
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.trialArranged,
        active: [
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.TRIAL_PAID,
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.trialCompleted,
        active: [
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.commenced,
        active: [
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
        activeLabel: [
          AC_BASE_REQUEST_STATUS.ACTIVE,
          AC_BASE_REQUEST_STATUS.CLOSED,
        ].includes(baseRequest?.status),
      },
      {
        label: STEP_STATUS_LABEL.completed,
        active: [AC_BASE_REQUEST_STATUS.CLOSED].includes(baseRequest?.status),
        activeLabel: [AC_BASE_REQUEST_STATUS.CLOSED].includes(
          baseRequest?.status,
        ),
      },
    ];
    return steps;
  }, [baseRequest]);

  const handleViewBooking = () => {
    navigate(APP_ROUTES.JOB.to);
  };

  useEffect(() => {
    if (!baseRequest) {
      navigate(APP_ROUTES.HOW_IT_WORKS.to);
    }
    // eslint-disable-next-line
  }, []);

  if (!baseRequest)
    return <EmptyUI content="You don’t have any request yet." />;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent={'space-between'}
        spacing={1}
      >
        <Stack>
          <Typography variant="Web_Title_22">Overview</Typography>
          <Typography variant="Web_Label_18">
            Overview of your request
          </Typography>
        </Stack>
        <Button variant="contained" onClick={handleViewBooking}>
          View Booking
        </Button>
      </Stack>
      <Stack overflow={'auto'}>
        <StatusPanel steps={listStep} />
      </Stack>
      <RequestOverview requestData={baseRequest} />
      <SittersOverView
        acBaseRequestSitters={baseRequest.ac_base_request_sitters}
      />
    </Stack>
  );
};

export default Overview;
